// Pure, import-free settings model for Clients -> Inactivity alert settings.
//
// The BACKEND is the source of truth. This module owns the form shape, the validation rule and
// the mapping to and from the API payload — nothing here persists anything in the browser, so a
// saved setting cannot silently diverge between two machines.
//
// Every threshold is a required, finite, whole integer between 0 and 3650 inclusive; zero is a
// legitimate value and the upper bound is the one the API enforces, so an out-of-range entry is
// refused before a request is made rather than coming back as a 422.

export type ClientInactivityAlertThresholdKey =
    | 'daysNotLoggedIn'
    | 'daysNoNewProjectsSubmitted'
    | 'weeksNoCommunications';

export type ClientInactivityAlertToggleKey = 'isEnabled' | 'isEmailEnabled' | 'isSlackEnabled' | 'isDashboardEnabled';

export type ClientInactivityAlertSettings = Record<ClientInactivityAlertThresholdKey, number> &
    Record<ClientInactivityAlertToggleKey, boolean>;

// The form keeps thresholds as raw strings so an invalid entry can be shown back to the user
export type ClientInactivityAlertSettingsForm = Record<ClientInactivityAlertThresholdKey, string> &
    Record<ClientInactivityAlertToggleKey, boolean>;

export type ClientInactivityAlertSettingsFormErrors = Partial<Record<ClientInactivityAlertThresholdKey, string>>;

export interface ClientInactivityAlertThresholdField {
    key: ClientInactivityAlertThresholdKey;
    label: string;
    inputId: string;
}

export interface ClientInactivityAlertToggleField {
    key: ClientInactivityAlertToggleKey;
    label: string;
}

export const THRESHOLD_FIELDS: ClientInactivityAlertThresholdField[] = [
    { key: 'daysNotLoggedIn', label: 'Days not logged in:', inputId: 'client-inactivity-days-not-logged-in' },
    {
        key: 'daysNoNewProjectsSubmitted',
        label: 'Days no new projects submitted:',
        inputId: 'client-inactivity-days-no-new-projects-submitted',
    },
    {
        key: 'weeksNoCommunications',
        label: 'Weeks no communications/comments:',
        inputId: 'client-inactivity-weeks-no-communications',
    },
];

export const TOGGLE_FIELDS: ClientInactivityAlertToggleField[] = [
    { key: 'isEnabled', label: 'Turn on client inactivity alerts:' },
    { key: 'isEmailEnabled', label: 'Send emails:' },
    { key: 'isSlackEnabled', label: 'Post on Slack:' },
    { key: 'isDashboardEnabled', label: 'Show in dashboard:' },
];

// Mirrors the defaults the API serves before anything was ever saved: every channel off, so a
// deployment that has never opened this page cannot start emailing anyone
export const DEFAULT_CLIENT_INACTIVITY_ALERT_SETTINGS: ClientInactivityAlertSettings = {
    daysNotLoggedIn: 30,
    daysNoNewProjectsSubmitted: 30,
    weeksNoCommunications: 4,
    isEnabled: false,
    isEmailEnabled: false,
    isSlackEnabled: false,
    isDashboardEnabled: false,
};

// The bound the API enforces. Kept in sync deliberately: a value the form accepts and the API
// refuses is a 422 the user cannot act on
export const MIN_THRESHOLD = 0;
export const MAX_THRESHOLD = 3650;

export const REQUIRED_ERROR_MESSAGE = 'Enter a value';
export const WHOLE_NUMBER_ERROR_MESSAGE = 'Enter a whole number of 0 or more';
export const OUT_OF_RANGE_ERROR_MESSAGE = `Enter a whole number no greater than ${MAX_THRESHOLD}`;

// Digits only: this is what rejects decimals, signs, exponents, spaces inside and letters
const WHOLE_NUMBER_PATTERN = /^[0-9]+$/;

export const parseWholeCount = (raw: unknown): number | null => {
    if (typeof raw === 'number') {
        return Number.isSafeInteger(raw) && raw >= 0 ? raw : null;
    }

    if (typeof raw !== 'string') {
        return null;
    }

    const trimmed = raw.trim();
    if (!WHOLE_NUMBER_PATTERN.test(trimmed)) {
        return null;
    }

    const value = Number(trimmed);
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
};

export const getWholeCountError = (raw: string): string | null => {
    if (typeof raw !== 'string' || raw.trim().length === 0) {
        return REQUIRED_ERROR_MESSAGE;
    }

    const value = parseWholeCount(raw);
    if (value === null) {
        return WHOLE_NUMBER_ERROR_MESSAGE;
    }

    return value > MAX_THRESHOLD ? OUT_OF_RANGE_ERROR_MESSAGE : null;
};

export const validateSettingsForm = (
    form: ClientInactivityAlertSettingsForm
): ClientInactivityAlertSettingsFormErrors => {
    const errors: ClientInactivityAlertSettingsFormErrors = {};

    THRESHOLD_FIELDS.forEach(({ key }) => {
        const error = getWholeCountError(form[key]);
        if (error !== null) {
            errors[key] = error;
        }
    });

    return errors;
};

export const hasSettingsFormErrors = (errors: ClientInactivityAlertSettingsFormErrors) =>
    Object.keys(errors).length > 0;

// Null whenever any threshold is invalid, so an invalid form can never be sent
export const parseSettingsForm = (form: ClientInactivityAlertSettingsForm): ClientInactivityAlertSettings | null => {
    if (hasSettingsFormErrors(validateSettingsForm(form))) {
        return null;
    }

    return {
        daysNotLoggedIn: parseWholeCount(form.daysNotLoggedIn) as number,
        daysNoNewProjectsSubmitted: parseWholeCount(form.daysNoNewProjectsSubmitted) as number,
        weeksNoCommunications: parseWholeCount(form.weeksNoCommunications) as number,
        isEnabled: form.isEnabled === true,
        isEmailEnabled: form.isEmailEnabled === true,
        isSlackEnabled: form.isSlackEnabled === true,
        isDashboardEnabled: form.isDashboardEnabled === true,
    };
};

export const toSettingsForm = (settings: ClientInactivityAlertSettings): ClientInactivityAlertSettingsForm => ({
    daysNotLoggedIn: `${settings.daysNotLoggedIn}`,
    daysNoNewProjectsSubmitted: `${settings.daysNoNewProjectsSubmitted}`,
    weeksNoCommunications: `${settings.weeksNoCommunications}`,
    isEnabled: settings.isEnabled,
    isEmailEnabled: settings.isEmailEnabled,
    isSlackEnabled: settings.isSlackEnabled,
    isDashboardEnabled: settings.isDashboardEnabled,
});

// The exact seven-key body the API accepts. Any extra key is a 422, so this builds the payload
// explicitly rather than spreading whatever the caller happens to hold
export const toSettingsPayload = (settings: ClientInactivityAlertSettings) => ({
    days_not_logged_in: settings.daysNotLoggedIn,
    days_no_new_projects_submitted: settings.daysNoNewProjectsSubmitted,
    weeks_no_communications: settings.weeksNoCommunications,
    is_enabled: settings.isEnabled,
    is_email_enabled: settings.isEmailEnabled,
    is_slack_enabled: settings.isSlackEnabled,
    is_dashboard_enabled: settings.isDashboardEnabled,
});

// Anything unreadable in the response falls back per field, so one unexpected value cannot
// blank the whole form
export const parseSettingsResource = (payload: unknown): ClientInactivityAlertSettings => {
    const defaults = DEFAULT_CLIENT_INACTIVITY_ALERT_SETTINGS;

    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
        return { ...defaults };
    }

    const resource = payload as Record<string, unknown>;

    const readCount = (key: ClientInactivityAlertThresholdKey, apiKey: string) => {
        const value = parseWholeCount(resource[apiKey]);
        return value === null || value > MAX_THRESHOLD ? defaults[key] : value;
    };
    const readFlag = (key: ClientInactivityAlertToggleKey, apiKey: string) =>
        typeof resource[apiKey] === 'boolean' ? (resource[apiKey] as boolean) : defaults[key];

    return {
        daysNotLoggedIn: readCount('daysNotLoggedIn', 'days_not_logged_in'),
        daysNoNewProjectsSubmitted: readCount('daysNoNewProjectsSubmitted', 'days_no_new_projects_submitted'),
        weeksNoCommunications: readCount('weeksNoCommunications', 'weeks_no_communications'),
        isEnabled: readFlag('isEnabled', 'is_enabled'),
        isEmailEnabled: readFlag('isEmailEnabled', 'is_email_enabled'),
        isSlackEnabled: readFlag('isSlackEnabled', 'is_slack_enabled'),
        isDashboardEnabled: readFlag('isDashboardEnabled', 'is_dashboard_enabled'),
    };
};

export interface ClientInactivityAlertRunOutcome {
    isDisabled: boolean;
    createdNoticeCount: number;
    supersededNoticeCount: number;
    suppressedNoticeCount: number;
    unchangedNoticeCount: number;
    evaluatedClientCount: number;
    emailRecipientCount: number;
}

export const parseRunResult = (payload: unknown): ClientInactivityAlertRunOutcome => {
    const resource = (payload === null || typeof payload !== 'object' ? {} : payload) as Record<string, unknown>;
    const readCount = (key: string) => {
        const value = parseWholeCount(resource[key]);
        return value === null ? 0 : value;
    };

    return {
        isDisabled: resource.is_disabled === true,
        createdNoticeCount: readCount('created_notice_count'),
        supersededNoticeCount: readCount('superseded_notice_count'),
        suppressedNoticeCount: readCount('suppressed_notice_count'),
        unchangedNoticeCount: readCount('unchanged_notice_count'),
        evaluatedClientCount: readCount('evaluated_client_count'),
        emailRecipientCount: readCount('email_recipient_count'),
    };
};

// What "Run alert check now" reports back. The disabled case is spelled out rather than shown
// as a bare success, so nobody reads "nothing happened" as "no inactive clients"
export const describeRunOutcome = (outcome: ClientInactivityAlertRunOutcome): string => {
    if (outcome.isDisabled) {
        return 'Client inactivity alerts are turned off, so no check ran and nothing was sent.';
    }

    const parts = [
        `${outcome.evaluatedClientCount} client${outcome.evaluatedClientCount === 1 ? '' : 's'} checked`,
        `${outcome.createdNoticeCount} new alert${outcome.createdNoticeCount === 1 ? '' : 's'}`,
    ];

    if (outcome.supersededNoticeCount > 0) {
        parts.push(`${outcome.supersededNoticeCount} updated`);
    }
    if (outcome.suppressedNoticeCount > 0) {
        parts.push(`${outcome.suppressedNoticeCount} already cleared`);
    }
    if (outcome.unchangedNoticeCount > 0) {
        parts.push(`${outcome.unchangedNoticeCount} unchanged`);
    }

    parts.push(`${outcome.emailRecipientCount} email recipient${outcome.emailRecipientCount === 1 ? '' : 's'}`);

    return `Check complete: ${parts.join(', ')}.`;
};

// One line per triggered condition, used by both warning surfaces
export const describeCondition = (condition: {
    label: string;
    unit: string;
    threshold: number;
    last_date: string | null;
}): string => {
    const unit = `${condition.unit}${condition.threshold === 1 ? '' : 's'}`;
    const last = condition.last_date === null ? 'never recorded' : `last on ${condition.last_date}`;

    return `${condition.label}: ${condition.threshold} ${unit} threshold, ${last}`;
};
